import * as React from "react";

import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { emptyRef, isChildOfType, nullElement } from "../../TypingUtils";
import { Sticky } from "../Sticky/Sticky";

import * as styles from "./Rows.styles";
import { StickyTableSidebar } from "./StickyTableSidebar";
import { MultiRowContainer } from "./MultilineRow/MultiRowContainer";

function checkBottomPadding(sidebar: null | JSX.Element, footer: null | JSX.Element): boolean {
    if (footer) {
        return false;
    }
    if (!sidebar || !isChildOfType(sidebar, StickyTableSidebar)) {
        return true;
    }
    const lastChild = React.Children.toArray(sidebar.props.children).slice(-1)[0];
    if (isChildOfType(lastChild, MultiRowContainer)) {
        return !lastChild.props.isAddButton || !lastChild.props.isPager;
    } else {
        return true;
    }
}

interface RowsProps {
    data: null | JSX.Element;
    header: null | JSX.Element;
    horizontalScroll?: number;
    innerWidth: number;
    paddingLeft: number;
    setData: (dataElement: HTMLDivElement) => void;
    sidebar: null | JSX.Element;
    pager: null | JSX.Element;
    footer: null | JSX.Element;
}

export class Rows extends React.PureComponent<RowsProps> {
    private readonly stop = React.createRef<HTMLDivElement>();
    public sticky: null | Sticky = emptyRef;

    public componentDidMount(): void {
        this.sticky?.setStop(this.stop.current ?? undefined);
    }

    public render(): JSX.Element {
        const isBottomPadding = checkBottomPadding(this.props.sidebar, this.props.footer);
        const paddingBottom = isBottomPadding ? 20 : 0;
        const header = this.props.header
            ? React.cloneElement(this.props.header, { horizontalScroll: this.props.horizontalScroll })
            : nullElement;
        const sidebar = this.props.sidebar
            ? React.cloneElement(this.props.sidebar, {
                  horizontalScroll: this.props.horizontalScroll,
                  paddingBottom: paddingBottom,
              })
            : nullElement;

        const pager = this.props.pager
            ? React.cloneElement(this.props.pager, {
                  horizontalScroll: this.props.horizontalScroll,
                  paddingBottom: paddingBottom,
              })
            : nullElement;

        const footer = this.props.footer
            ? React.cloneElement(this.props.footer, {
                  horizontalScroll: this.props.horizontalScroll,
                  setRefSticky: this.refSticky,
              })
            : nullElement;

        const contentStyle: React.CSSProperties = { overflowX: "hidden" };

        if (this.props.innerWidth > 0) {
            contentStyle.width = this.props.innerWidth;
        }

        return (
            <div>
                {header}
                <div css={styles.footerStop} ref={this.stop} />
                <div
                    css={themed(
                        styles.contentTable,
                        this.props.horizontalScroll ? styles.scrolledTable : styles.bottomTable
                    )}>
                    {sidebar}
                    <div ref={this.props.setData} style={contentStyle}>
                        <div
                            style={{ paddingLeft: sidebar ? 0 : this.props.paddingLeft, paddingBottom: paddingBottom }}>
                            {this.props.data}
                        </div>
                    </div>
                </div>
                {pager}
                {footer}
            </div>
        );
    }

    private readonly refSticky = (el: null | Sticky) => (this.sticky = el);
}
